import React from 'react';
import styled from 'styled-components';

import { InputAdornment, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Command, tabActions } from '@store/slices';
import { useDispatch, useSelector } from 'react-redux';
import { State } from '@store/index';
import { useParams } from 'react-router-dom';
import { Label } from '@common/Label';
import { NodeField } from '@components/Flow/Nodes';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: 8px;
`;

const EndpointContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  & .MuiInputAdornment-root {
    margin-right: 1px;

    p {
      color: ${({ theme }) => theme.primary};
    }
  }
`;

interface Props {
  command: Command;
  field: NodeField;
}

export const WorkflowEndpoint: React.FC<Props> = ({ command, field }) => {
  const dispatch = useDispatch();

  const { service_id, workflow_id } = useParams();
  const { state } = command;

  const handleChange = (event: React.MouseEvent<HTMLElement>, newMethod: string) => {
    if (newMethod && newMethod !== state.method) {
      dispatch(
        tabActions.nodeInputUpdate({
          nodeId: command.node.id,
          fieldId: field.id,
          value: newMethod,
        })
      );

      dispatch(tabActions.setSaved(false));
    }
  };

  const flows = useSelector((state: State) => state.flow.flows);
  const path = useSelector((state: State) => state.tabs.active.service_path);

  const convert = (input: string) => {
    if (!input) return input;

    let formatted = input.replace(/ /g, '_');
    formatted = formatted.replace(/[^a-zA-Z0-9_\-]/g, '');
    return formatted?.toLowerCase();
  };

  const updateEndpoint = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = convert(e.target.value);

    dispatch(tabActions.setSaved(false));
    dispatch(tabActions.setServicePath(value));
  };

  const invalidServicePath = Boolean(
    flows.filter((flow) => flow.service_path === convert(path || '') && flow.flow_id !== workflow_id).length >= 1
  );

  return (
    <Container>
      <Label
        title='Endpoint'
        tooltip='This is the path used to execute this endpoint with the method of the trigger node (GET, POST). Each endpoint is given a random identifier, but you can give it a specific string name by modifying it here.'
      />
      <EndpointContainer>
        <ToggleButtonGroup
          color='primary'
          size='small'
          value={state?.method?.toLowerCase() || ''}
          exclusive
          onChange={handleChange}
          fullWidth
        >
          <ToggleButton value='get'>GET</ToggleButton>
          <ToggleButton value='post'>POST</ToggleButton>
          <ToggleButton value='put'>PUT</ToggleButton>
          <ToggleButton value='patch'>PATCH</ToggleButton>
          <ToggleButton value='delete'>DELETE</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size='small'
          value={path === null ? workflow_id : path}
          placeholder='endpoint name'
          onChange={updateEndpoint}
          helperText={invalidServicePath ? 'This pathname is already being used by another endpoint.' : undefined}
          error={invalidServicePath}
          InputProps={{
            startAdornment: <InputAdornment position='start'>{`/execute/${service_id}/`}</InputAdornment>,
          }}
        />
      </EndpointContainer>
    </Container>
  );
};
